import { createFeature, createReducer, on } from '@ngrx/store';
import { DashboardActions } from '../../actions/dashboard/dashboard.actions';
import { UserActions } from '@store/actions/user/user.actions';
import { DashboardProviderProd } from '@models/dashboard-providers.model';
import { DashboardMeasureDetailProd } from '@models/dashboard-measure-details.model';
import { DashboardMeasureLocation } from '@models/dashboard-location.model';
import { GaugeData } from '@models/gauges.model';
import { sampleGaugesProd } from '@components/mipsgauage/gauage-mockdata';
import { MonthlyTrend } from '@models/monthly-trend.model';
import { getCurrentPerformanceYear } from '../../../constants/mips-constants';
import { DashboardMipsScoreBlock, DashboardMipsRequirements, DashboardTopMeasure } from '@models/dashboard-mips-scores.model';
import { DashboardMeasureDecile } from '@models/dashboard-measure-deciles.model';
import { CalculateMipsReweightingResponse } from '@models/mips-reweighting-score.model';

export const dashboardFeatureKey = 'dashboard';

export interface DashboardFilterState {
  measureSelection: string[];
  clinician: string[];
  location: string[];
  selectGroup: string;
  measureYear: string;
  isTinAggregationEnabled: boolean;
}

export interface State {
  showBenchmarks: boolean,
  selectedTab: string,
  selectedDetailsTab: string,
  showMeasureDetails: boolean,
  showPatientDetails: boolean,
  selectedIndex: number | null,
  selectedMeasure: DashboardMeasureDetailProd | null,
  isSmallPractice: boolean,
  measures: DashboardMeasureDetailProd[],
  uniqueMeaures: any[],
  gauges: GaugeData[],
  providers: DashboardProviderProd[],
  locations: DashboardMeasureLocation[],
  isLoading: boolean,
  isMonthlyTrendLoading: boolean,
  monthlyTrend: MonthlyTrend[],
  selectedMonthlyTrendYears: number[],
  mipsScores: DashboardMipsScoreBlock | null,
  mvpScores: DashboardMipsScoreBlock | null,
  mipsRequirements: DashboardMipsRequirements | null,
  topTraditionalMeasures: DashboardTopMeasure[],
  topMvpMeasures: DashboardTopMeasure[],
  mipsScoresLoading: boolean,
  programYear: number,
  favoriteMeasureCodes: string[],
  favoritesError: string | null,
  isMultiOrgTin: boolean,
  savedFilters: DashboardFilterState | null,
  deciles: DashboardMeasureDecile[],
  decilesYear: number | null,
  reweighting: CalculateMipsReweightingResponse | null,
  reweightingLoading: boolean,
}

export const initialState: State = {
  showBenchmarks: true,
  selectedTab: "all",
  selectedDetailsTab: "performanceData",
  showMeasureDetails: false,
  showPatientDetails: false,
  selectedIndex: null,
  selectedMeasure: null,
  isSmallPractice: false,
  measures: [],
  uniqueMeaures: [],
  gauges: sampleGaugesProd,
  providers: [],
  locations: [],
  isLoading: false,
  isMonthlyTrendLoading: false,
  monthlyTrend: [],
  selectedMonthlyTrendYears: [getCurrentPerformanceYear()],
  mipsScores: null,
  mvpScores: null,
  mipsRequirements: null,
  topTraditionalMeasures: [],
  topMvpMeasures: [],
  mipsScoresLoading: false,
  programYear: getCurrentPerformanceYear(),
  favoriteMeasureCodes: [],
  favoritesError: null,
  isMultiOrgTin: false,
  savedFilters: null,
  deciles: [],
  decilesYear: null,
  reweighting: null,
  reweightingLoading: false,
};

export const reducer = createReducer(
  initialState,
  on(DashboardActions.getDashboardLocationsSuccess, (state, action) =>({
    ...state,
    locations: action.locations
  })),

  on(DashboardActions.getDashboardProvidersSuccess, (state, action) =>({
    ...state,
    providers: action.providers
  })),

  on(DashboardActions.getDashboardMeasureSummariesSuccess, (state, action) =>({
    ...state,
    measures: action.measures
  })),
  on(DashboardActions.getDashboardMeasureSummariesFailure, (state) => ({
    ...state,
    measures: []
  })),

  on(DashboardActions.loadDashboardMipsScores, (state) => ({
    ...state,
    mipsScoresLoading: true,
  })),
  on(DashboardActions.loadDashboardMipsScoresSuccess, (state, { data }) => ({
    ...state,
    measures: data.measures,
    mipsScores: data.mipsScores,
    mvpScores: data.mvpScores,
    mipsRequirements: data.mipsRequirements,
    topTraditionalMeasures: data.topTraditionalMeasures,
    topMvpMeasures: data.topMvpMeasures,
    mipsScoresLoading: false,
  })),
  on(DashboardActions.loadDashboardMipsScoresFailure, (state) => ({
    ...state,
    mipsScoresLoading: false,
  })),

  on(DashboardActions.setSmallPractice, (state, action) => ({
    ...state,
    isSmallPractice: action.isSmallPractice,
  })),

  on(DashboardActions.clickOnMeasure, (state, action) => {
    if (state.selectedIndex === action.index && state.showMeasureDetails) {
      return {
        ...state,
        showMeasureDetails: false,
        selectedIndex: null,
        selectedMeasure: null,
        monthlyTrend: [],
        selectedMonthlyTrendYears: [state.programYear]
      }
    } else {
      return {
        ...state,
        selectedDetailsTab: 'performanceData',
        selectedMeasure: action.measure,
        selectedIndex: action.index,
        showMeasureDetails: true,
        monthlyTrend: [],
        selectedMonthlyTrendYears: [state.programYear]
      }
    }
  }),

  on(DashboardActions.toggleMeasureExpansion, (state, action) => {
    const regex = /[a-zA-Z]+[-]\d+/;
    const found = action.measure.name?.match(regex);
    const basename = found != null && found.length > 0 ? found[0] : '';
    const measureIndex = state.measures.indexOf(action.measure);

    return {
      ...state,
      measures:
      state.measures.map(
        (value, index) => {
          let showChild = value.showChild;
          let expanded = value.expanded;

          let start = value.name?.match(regex) ?? [''];
          let startswith = start[0];
          if (startswith === basename) {
            showChild = !value.showChild;
          }

          if (index === measureIndex) {
            expanded = !value.expanded;
            showChild = true;
          }
          return {
            ...value,
            showChild: showChild,
            expanded: expanded
          }
        })
    }
  }),

  on(DashboardActions.selectDetailsTab, (state, action) => ({
    ...state,
    selectedDetailsTab: action.tab,
    selectedMonthlyTrendYears: action.tab === 'monthlyTrend' ? [state.programYear] : state.selectedMonthlyTrendYears,
    isMonthlyTrendLoading: action.tab === 'monthlyTrend'
  })),

  on(DashboardActions.getMeasureMonthlyTrendSuccess, (state, action) => ({
    ...state,
    monthlyTrend: action.trend,
    isMonthlyTrendLoading: false
  })),

  on(DashboardActions.getMeasureMonthlyTrendFailure, (state) => ({
    ...state,
    isMonthlyTrendLoading: false
  })),

  on(DashboardActions.monthlyTrendMonthsChanged, (state, action) => ({
    ...state,
    selectedMonthlyTrendYears: action.years,
    monthlyTrend: [],
    isMonthlyTrendLoading: true
  })),

  on(DashboardActions.applyFilters, (state, action) => ({
    ...state,
    programYear: action.filters.measureYear
  })),

  on(DashboardActions.loadMeasureFavoritesSuccess, (state, action) => ({
    ...state,
    favoriteMeasureCodes: action.measureCodes,
  })),

  on(DashboardActions.addMeasureFavoriteSuccess, (state, action) => ({
    ...state,
    favoriteMeasureCodes: [...state.favoriteMeasureCodes, action.measureCode],
  })),

  on(DashboardActions.removeMeasureFavoriteSuccess, (state, action) => ({
    ...state,
    favoriteMeasureCodes: state.favoriteMeasureCodes.filter(code => code !== action.measureCode),
  })),

  on(
    DashboardActions.loadMeasureFavoritesFailure,
    DashboardActions.addMeasureFavoriteFailure,
    DashboardActions.removeMeasureFavoriteFailure,
    (state, action) => ({
      ...state,
      favoritesError: action.error,
    })
  ),

  on(DashboardActions.loadDashboardDecilesSuccess, (state, { deciles, year }) => ({
    ...state,
    deciles,
    decilesYear: year,
  })),

  on(DashboardActions.calculateMipsReweighting, (state) => ({
    ...state,
    reweightingLoading: true,
  })),
  on(DashboardActions.calculateMipsReweightingSuccess, (state, { data }) => ({
    ...state,
    reweighting: data,
    reweightingLoading: false,
  })),
  on(DashboardActions.calculateMipsReweightingFailure, (state) => ({
    ...state,
    reweightingLoading: false,
  })),

  on(DashboardActions.setIsMultiOrgTin, (state, action) => ({
    ...state,
    isMultiOrgTin: action.isMultiOrgTin,
  })),

  on(UserActions.logoutButtonPressed, () => initialState),

  on(UserActions.setUserOrganizationId, (state) => ({
    ...state,
    savedFilters: null,
  })),

  on(DashboardActions.saveFilterState, (state, action) => ({
    ...state,
    savedFilters: {
      measureSelection: action.measureSelection,
      clinician: action.clinician,
      location: action.location,
      selectGroup: action.selectGroup,
      measureYear: action.measureYear,
      isTinAggregationEnabled: action.isTinAggregationEnabled,
    },
  })),
);

export const dashboardFeature = createFeature({
  name: dashboardFeatureKey,
  reducer,
});
